import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { REACTION_EMOJIS } from '@storychain/shared';
import type { ReactionEmoji, ReactionTargetType, ReactionCounts } from '@storychain/shared';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth.store';
import { DEFAULT_UI_LOCALE, isSupportedUILocale, withLocale } from '../locale';

interface ReactionsProps {
  targetType: ReactionTargetType;
  targetId: string;
  reactions: ReactionCounts | undefined;
  myReaction: ReactionEmoji | null;
}

export const Reactions: React.FC<ReactionsProps> = ({ targetType, targetId, reactions, myReaction }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const uiLocale = (() => {
    const first = location.pathname.split('/')[1];
    return isSupportedUILocale(first) ? first : DEFAULT_UI_LOCALE;
  })();
  const loginHref = withLocale(uiLocale, '/login');
  const [localReactions, setLocalReactions] = useState<ReactionCounts>(reactions || {});
  const [localMine, setLocalMine] = useState<ReactionEmoji | null>(myReaction);

  const mutation = useMutation({
    mutationFn: (emoji: ReactionEmoji | null) => api.setReaction(targetType, targetId, emoji),
    onSuccess: (state) => {
      setLocalReactions(state.reactions as ReactionCounts);
      setLocalMine(state.myReaction);
    },
  });

  const handleTap = (emoji: ReactionEmoji) => {
    if (!isAuthenticated || mutation.isPending) return;
    const next = localMine === emoji ? null : emoji;

    // Optimistic update
    const nextCounts: ReactionCounts = { ...localReactions };
    if (localMine) nextCounts[localMine] = Math.max(0, (nextCounts[localMine] || 1) - 1);
    if (next) nextCounts[next] = (nextCounts[next] || 0) + 1;
    setLocalReactions(nextCounts);
    setLocalMine(next);

    mutation.mutate(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {REACTION_EMOJIS.map((emoji) => {
        const count = localReactions[emoji] || 0;
        const picked = localMine === emoji;
        return (
          <button
            key={emoji}
            onClick={() => handleTap(emoji)}
            disabled={!isAuthenticated || mutation.isPending}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border transition text-sm ${
              picked
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            } ${!isAuthenticated ? 'opacity-60 cursor-not-allowed' : ''}`}
            title={isAuthenticated ? (picked ? 'Remove reaction' : `React ${emoji}`) : 'Sign in to react'}
          >
            <span className="text-base leading-none">{emoji}</span>
            {count > 0 && <span className="tabular-nums text-xs">{count}</span>}
          </button>
        );
      })}
      {!isAuthenticated && (
        <Link to={loginHref} className="text-xs text-primary-600 hover:underline ml-1">
          Sign in to react
        </Link>
      )}
    </div>
  );
};
